import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { AppView, Button, rem, withAnimatedNavigationHeader, } from 'design-system';
import { ContactItem, SelectedContactsList, } from 'features/contacts/components';
import { Keyboard, SectionList, StyleSheet, View, } from 'react-native';
import SearchBar from 'design-system/SearchBar';
import Animated, { interpolateColor, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import useColors from 'hooks/useColors';
import { useOnSearch } from 'features/contacts/hooks/useOnSearch';
import { useMapToSections } from 'features/contacts/hooks/useMapToSections';
import ListEmptyView from 'design-system/ListEmptyView';
import ListLoader from 'design-system/ListLoader';
import SectionHeader from 'design-system/SectionHeader';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useFetch } from 'hooks';
import FlatListItemCard from 'design-system/FlatListItemCard';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import { selectUser } from 'reducers/selectors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { useContactInvite } from 'features/contacts/hooks/useContactInvite';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { getMySpaces, getSpaceInfo, invitePeopleToSpace } from './actions';
import { selectSpaceInvitesWithStatus } from './reducers/selectors';
const strings = {
    buttonContinue: 'Continue',
    buttonInvite: 'Invite',
    buttonAdd: 'Add',
    buttonRemove: 'Remove',
    buttonAdded: 'Added',
    noResultTitle: 'Try searching for another name',
    subTitle: 'Enable contacts to be able to add your friends and family to this space to organise financial life together.',
    addError: 'You can’t add yourself to this space',
};
const AnimatedSectionList = Animated.createAnimatedComponent(SectionList);
const keyExtractor = (item, index) => (item.handle || item.recordID).toString() + index;
const SpaceInviteScreen = withAnimatedNavigationHeader(({ route, navigation }) => {
    const scrollY = useSharedValue(0);
    const { selectedContacts, onPressInvite, removeFromSelected, selectedContactsObj, } = useContactInvite();
    const searchBarRef = useRef(null);
    const colors = useColors();
    const space = 'space' in route.params ? route.params.space : undefined;
    const fromEdit = 'fromEdit' in route.params ? route.params.fromEdit : false;
    const { pending: pendingInvites } = useAppSelector((store) => selectSpaceInvitesWithStatus(store, space?.id || -1));
    const textInputStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors.background.dark, colors.cards.onDark, scrollY]);
    const { remoteContacts, searchFilter, handleSearch, isLoading, handleReset, } = useOnSearch();
    const onChangeSearchTerm = useCallback((newValue) => {
        handleSearch(newValue);
    }, [handleSearch]);
    const sections = useMapToSections(searchFilter, remoteContacts);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const [fetch] = useFetch({ muteAlert: true });
    const [spaceUsers, setSpaceUsers] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: route.params.fromEdit ? 'Add friends' : '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, route.params.fromEdit]);
    useEffect(() => {
        fetch(getSpaceInfo(route.params.space.id)).then((result) => {
            setSpaceUsers(result.users || []);
        });
    }, [route.params.space]);
    const user = useAppSelector(selectUser);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, {
        marginTop: rem(8),
    });
    const onAdd = useCallback((item) => {
        if (item.handle?.toLowerCase() === user.urlHandle.toLowerCase()) {
            Modal.showError(strings.addError, () => { }, 'Hold on 💫', 'Okay');
            return;
        }
        onPressInvite(item);
    }, [onPressInvite, user.urlHandle]);
    const renderItem = useCallback(({ item, index, section }) => {
        const formattedHandle = item.handle?.toLowerCase();
        const formattedItem = { ...item, handle: formattedHandle };
        const active = formattedHandle && !!selectedContactsObj[formattedHandle];
        let title = strings.buttonInvite;
        const isInSpace = spaceUsers.find((u) => u.handle?.toLowerCase() === formattedHandle);
        const isInvited = pendingInvites.find((i) => i.member.handle?.toLowerCase() === formattedHandle);
        if (item.isEmmaUser)
            title = strings.buttonAdd;
        if (active)
            title = strings.buttonRemove;
        return (<FlatListItemCard index={index} style={{ backgroundColor: colors.cards.onDark }} itemsLength={section.data.length}>
            <ContactItem contact={formattedItem}>
              {isInSpace || isInvited ? (<Text Text-14 Gray-50 style={styles.added}>
                  {strings.buttonAdded}
                </Text>) : (<Button {...(active ? { gray: true } : { brandReversed: true })} hug square title={title} onPress={() => active
                    ? removeFromSelected(formattedItem)
                    : onAdd(formattedItem)} style={styles.button}/>)}
            </ContactItem>
          </FlatListItemCard>);
    }, [
        selectedContactsObj,
        spaceUsers,
        pendingInvites,
        colors.cards.onDark,
        removeFromSelected,
        onAdd,
    ]);
    const renderContent = useMemo(() => {
        let ListEmptyComponent = null;
        if (!sections.length && !isLoading) {
            ListEmptyComponent = (<ListEmptyView happy alt style={styles.empty} onCard title="No results" subTitle="Try searching for another @EmmaTag or name"/>);
        }
        if (!sections.length && isLoading) {
            ListEmptyComponent = <ListLoader smallMargin/>;
        }
        return (<AnimatedSectionList renderSectionHeader={({ section: { title } }) => (<SectionHeader verticalMargins horizontalMargins onLight>
              {title}
            </SectionHeader>)} sections={sections} onScroll={scrollHandler} stickySectionHeadersEnabled={false} renderItem={renderItem} keyExtractor={keyExtractor} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag" ListEmptyComponent={ListEmptyComponent} contentContainerStyle={contentContainerStyle}/>);
    }, [sections, isLoading, scrollHandler, renderItem, contentContainerStyle]);
    const handleSave = useCallback(async () => {
        setIsFetching(true);
        Keyboard.dismiss();
        const spaceId = space?.id;
        if (selectedContacts.length > 0 && spaceId) {
            const handles = selectedContacts
                .map((contact) => contact.handle)
                .filter((handle) => !!handle);
            await dispatch(invitePeopleToSpace(spaceId, handles));
            dispatch(getMySpaces());
        }
        if (fromEdit && space) {
            dispatch(getSpaceInfo(space.id));
            navigation.pop(1);
            navigation.navigateDeprecated('EditSpace', { id: space.id, space });
            return;
        }
        navigation.navigateDeprecated('Success', {
            title: 'Success',
            subTitle: 'The Space has been set up! You can\nstart organising financial life with\nmembers!',
            onContinue: () => {
                navigation.pop(3);
                navigation.navigateDeprecated('MySpaces');
                return true;
            },
        });
        setIsFetching(false);
    }, [selectedContacts, space, fromEdit]);
    return (<AppView>
        <View>
          {!fromEdit && (<ScreenInfoHeader title="Add friends" style={{ paddingBottom: 0 }}/>)}
          <SelectedContactsList onPress={removeFromSelected} contacts={selectedContacts}/>
          <SearchBar alwaysShowCancel={false} ref={searchBarRef} placeholder="Search @EmmaTag or name" textInputBgStyles={textInputStyle} containerStyle={styles.search} onChangeSearchTerm={onChangeSearchTerm} onCancel={handleReset} autoCapitalize="none"/>
        </View>

        {renderContent}

        <Button brand floating title={strings.buttonContinue} onPress={handleSave} isFetching={isFetching}/>
      </AppView>);
}, ({ colors }) => ({
    hasCustomTitle: true,
    title: 'Add friends',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    search: {
        marginBottom: rem(4),
    },
    button: {
        marginLeft: rem(12),
        paddingHorizontal: rem(8),
    },
    empty: {
        marginTop: rem(8),
    },
    added: {
        marginRight: rem(15),
    },
});
export default SpaceInviteScreen;
